import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Text, NewSeat, Checkbox } from '@reservamos/elements';
import cn from 'utils/cn';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';

import cancelledIcon from 'images/icons/cancelled.svg';
import ticketIcon from 'images/icons/iconTicket.svg';

const STATUS_MAP = {
  default: {
    textKey: 'select',
    // Split so a display-only card can take the surface without the affordances: the hover tint
    // and the pointer cursor promise a click that a card with no `onClickedTicket` never answers.
    container: 'bg-white border-transparent shadow-sm',
    interactive:
      'group cursor-pointer hover:bg-accent-regular/10 hover:border-accent-regular/30 hover:shadow-md',
    button: 'underline text-black group-hover:font-bold',
    footer: 'group-hover:bg-white',
  },
  canceled: {
    textKey: 'canceled',
    icon: cancelledIcon,
    container: 'bg-gray100',
    footer: 'bg-gray100',
    button: 'font-bold text-error-strong',
  },
  selected: {
    textKey: 'selected',
    container:
      'group cursor-pointer bg-white border-2 border-dashed border-success-regular/70 shadow-sm hover:bg-[#66BA5B]/15 hover:border-[#66BA5B]/70 hover:shadow-md',
    footer: 'bg-success/20 group-hover:bg-success/15',
    button: 'font-bold text-success-regular group-hover:underline',
  },
  unavailable: {
    textKey: 'unavailable',
    container:
      'cursor-not-allowed bg-grayMedium border-transparent opacity-75 select-none before:bg-grayMedium after:bg-grayMedium',
    footer: 'bg-grayMedium',
    button: 'text-grayStrong font-normal cursor-not-allowed',
    seatType: 'disabled',
  },
};

const propTypes = {
  status: PropTypes.oneOf(['default', 'canceled', 'selected', 'unavailable']),
  userName: PropTypes.string,
  floorNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  seatNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serviceName: PropTypes.string,
  ticketNumber: PropTypes.string,
  ticketKey: PropTypes.string,
  avatarSrc: PropTypes.string,
  selectable: PropTypes.bool,
  onClickedTicket: PropTypes.func,
  selectedTicketKey: PropTypes.string,
  selectedTicketKeys: PropTypes.arrayOf(PropTypes.string),
  statusLabel: PropTypes.string,
  badgeLabel: PropTypes.string,
};

/**
 * TicketSummaryCard component.
 *
 * Renders one ticket. Every detail below the passenger name is optional: a detail whose value the
 * tenant's payload does not carry is omitted together with its label, never rendered as an empty
 * row or a placeholder. Floor and service additionally follow SHOW_ENHANCED_TICKET_DATA, matching
 * the legacy exchange ticket list so a tenant sees the same details it sees today.
 *
 * The card holds no brand assets: the caller resolves the avatar and passes it in, and the card
 * renders without one when it gets none.
 *
 * @param {object} props - Component props.
 * @param {string} [props.status] - 'default', 'canceled', 'selected' or 'unavailable'.
 * @param {string} [props.userName] - Passenger name.
 * @param {string|number} [props.floorNumber] - Bus floor; omitted when absent.
 * @param {string|number} [props.seatNumber] - Seat number; omitted when absent.
 * @param {string} [props.serviceName] - Service name; omitted when absent.
 * @param {string} [props.ticketNumber] - Ticket number shown in the badge.
 * @param {string} [props.ticketKey] - Unique key used for selection.
 * @param {string} [props.avatarSrc] - Passenger artwork resolved by the caller.
 * @param {boolean} [props.selectable] - When false the card is display-only: no control, no footer.
 * @param {Function} [props.onClickedTicket] - Receives the ticket key when the card is toggled.
 * @param {string} [props.selectedTicketKey] - Selected key for single selection.
 * @param {Array<string>} [props.selectedTicketKeys] - Selected keys for multi selection.
 * @param {string} [props.statusLabel] - Replaces the wording the status would derive. A caller
 *   whose backend names the state itself passes that name, so the card shows the traveller what
 *   their provider called it rather than the card's own vocabulary.
 * @param {string} [props.badgeLabel] - A short provider-assigned state shown beside the name, for
 *   a ticket that carries one while still being selectable.
 * @returns {JSX.Element}
 */
const TicketSummaryCard = ({
  status = 'default',
  userName = '',
  floorNumber = '',
  seatNumber = '',
  serviceName = '',
  ticketNumber = '',
  ticketKey = '',
  avatarSrc = '',
  selectable = true,
  onClickedTicket,
  selectedTicketKey = '',
  selectedTicketKeys,
  statusLabel: statusLabelOverride = '',
  badgeLabel = '',
}) => {
  const { t } = useTranslation('exchange');
  const features = useWhitelabelFeatures();
  const showEnhancedTicketData = Boolean(features.SHOW_ENHANCED_TICKET_DATA);

  const isCancelled = status === 'canceled';
  const isUnavailable = status === 'unavailable';
  const isMultiSelect = Array.isArray(selectedTicketKeys);
  const isSelected = Boolean(
    isMultiSelect
      ? selectedTicketKeys.some((key) => String(key) === String(ticketKey))
      : ticketKey && selectedTicketKey && String(ticketKey) === String(selectedTicketKey),
  );

  const isSelectable = selectable && !isCancelled && !isUnavailable && Boolean(onClickedTicket);

  const effectiveStatus = isSelected && !isCancelled && !isUnavailable ? 'selected' : status;
  const statusMap = STATUS_MAP[effectiveStatus] ?? STATUS_MAP.default;
  const statusLabel = statusLabelOverride || t(`ticket_status.${statusMap.textKey}`);

  // Every detail is optional; an absent value drops the whole block, label included.
  const showSeat = seatNumber !== '' && seatNumber != null;
  const showFloor = showEnhancedTicketData && floorNumber !== '' && floorNumber != null;
  const showService = showEnhancedTicketData && Boolean(serviceName);
  // The avatar rides the last row that has content: the service block when the tenant shows one,
  // the seat row otherwise. A tenant without service data used to get an extra row holding nothing
  // but the artwork, which opened a band of empty card between the seat and the ticket number.
  const showDetailsRow = showSeat || showFloor || Boolean(avatarSrc);
  // Display-only cards carry no status affordance: no checkbox, no "select" label, no footer.
  const showStatusAffordance = selectable;

  /**
   * Toggle selection from the checkbox or the status control.
   * @param {React.SyntheticEvent} [event] - Originating event.
   */
  const applyToggle = (event) => {
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    if (!isSelectable || !onClickedTicket || !ticketKey) return;
    if (isMultiSelect) {
      onClickedTicket(ticketKey);
      return;
    }
    onClickedTicket(isSelected ? '' : ticketKey);
  };

  /**
   * Passenger artwork in its circle.
   *
   * Written once and placed twice: the two layouts put it on different lines — beside the seat on
   * mobile, beside the service block from `md` up — and each call hides itself on the other's
   * breakpoint, so only one is ever visible.
   *
   * @param {string} [visibilityClassName] - Breakpoint classes for the layout placing it.
   * @returns {JSX.Element}
   */
  const renderAvatar = (visibilityClassName) => (
    <div
      className={cn(
        'flex-shrink-0 items-center justify-center rounded-full p-1.5 ml-auto',
        isUnavailable ? 'bg-gray100' : 'bg-primary-regular/40',
        visibilityClassName,
      )}
    >
      <img src={avatarSrc} className="w-8 h-8" alt="" />
    </div>
  );

  /**
   * Cancel icon, selection checkbox, or nothing at all when the card is display-only.
   * @returns {JSX.Element|null}
   */
  const renderCheckboxOrIcon = () => {
    if (isCancelled) {
      return <img src={statusMap.icon} className="w-4 h-4" alt="" />;
    }
    if (!selectable) return null;
    return (
      <Checkbox
        id={`ticket-checkbox-${ticketKey || ticketNumber}`}
        name={`ticket-checkbox-${ticketKey || ticketNumber}`}
        onChange={applyToggle}
        checked={isSelected}
        readOnly={!isSelectable}
      />
    );
  };

  return (
    <div
      className={cn(
        'relative h-full w-full min-w-0 rounded-2xl overflow-visible border transition-all duration-200 ease-out',
        statusMap.container,
        isSelectable && statusMap.interactive,
        "before:content-[''] before:absolute before:top-[40px] before:left-0 before:-translate-x-1/2 before:w-[20px] before:h-[20px] before:rounded-full before:z-[11] before:bg-grayExtraLight max-md:before:hidden",
        "after:content-[''] after:absolute after:top-[40px] after:right-0 after:translate-x-1/2 after:w-[20px] after:h-[20px] after:rounded-full after:z-[11] after:bg-grayExtraLight max-md:after:hidden",
      )}
    >
      <div className="flex flex-col gap-3 md:gap-4 p-4">
        <div className="flex items-start justify-between gap-2 w-full min-w-0">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 pr-1">
            <Text weight="bold" color="grayMedium" className="min-w-0 break-words">
              {userName}
            </Text>
            {Boolean(badgeLabel) && (
              <span className="rounded-full bg-success/20 px-2 py-0.5 text-xs font-semibold text-success-strong">
                {badgeLabel}
              </span>
            )}
          </div>
          <div className="flex-shrink-0 pt-0.5">{renderCheckboxOrIcon()}</div>
        </div>

        {showDetailsRow && (
          <div className="flex items-center w-full">
            {showSeat && (
              <div className="flex flex-auto items-center gap-2 min-w-0">
                <Text color={isUnavailable ? 'grayStrong' : undefined}>{t('labels.seat')}</Text>
                <NewSeat
                  isSmall
                  type={isUnavailable && statusMap.seatType ? statusMap.seatType : 'selected'}
                  number={seatNumber}
                />
              </div>
            )}

            {showFloor && (
              <div className="flex-auto text-right flex-shrink-0">
                <Text color={isUnavailable ? 'grayStrong' : undefined}>
                  {t('labels.floor', { floor: floorNumber })}
                </Text>
              </div>
            )}

            {Boolean(avatarSrc) && renderAvatar(showService ? 'flex md:hidden' : 'flex')}
          </div>
        )}

        {showStatusAffordance && (
          <div className="md:hidden w-full text-center">
            {isSelectable ? (
              <button
                type="button"
                className={cn(
                  'w-full border-0 bg-transparent p-0 text-center text-sm leading-snug cursor-pointer select-none',
                  statusMap.button,
                )}
                onClick={applyToggle}
              >
                {statusLabel}
              </button>
            ) : (
              <p className={cn('text-sm leading-snug', statusMap.button)}>{statusLabel}</p>
            )}
          </div>
        )}

        {showService && (
          <div className="hidden md:flex items-center justify-between w-full">
            <div className="flex flex-col min-w-0">
              <Text color={isUnavailable ? 'grayStrong' : undefined}>{t('labels.service')}</Text>
              <Text
                color={isUnavailable ? 'grayStrong' : 'primary'}
                weight={isUnavailable ? 'normal' : 'bold'}
              >
                {serviceName}
              </Text>
            </div>

            {Boolean(avatarSrc) && renderAvatar('hidden md:flex')}
          </div>
        )}

        {/* The ticket number shows at every width — on a phone it is how the traveller tells which
            ticket this is — as a pill hugging its text there, and a full-width band from `md` up. */}
        {Boolean(ticketNumber) && (
          <div
            className={cn(
              'relative z-20 flex self-start md:self-auto md:justify-center md:w-full rounded-2xl px-3 py-1 md:py-2 md:my-2',
              isUnavailable ? 'bg-gray100' : 'bg-primary-regular/10',
            )}
          >
            <p
              className={cn(
                'flex items-center gap-2 text-sm min-w-0',
                isUnavailable ? 'text-grayStrong' : 'text-primary-strong',
              )}
            >
              <img src={ticketIcon} className="w-5 h-5 flex-shrink-0" alt="" />
              <span className="truncate">
                {t('labels.ticket_no')} {ticketNumber}
              </span>
            </p>
          </div>
        )}
      </div>

      {showStatusAffordance && (
        <div
          className={cn(
            'hidden md:flex justify-center w-full py-3 rounded-b-2xl',
            statusMap.footer,
          )}
        >
          <button
            type="button"
            className={cn(
              'px-6 py-2 rounded-xl bg-transparent max-w-full',
              statusMap.button,
              isSelectable && 'cursor-pointer',
            )}
            onClick={isSelectable ? applyToggle : undefined}
          >
            {statusLabel}
          </button>
        </div>
      )}
    </div>
  );
};

TicketSummaryCard.propTypes = propTypes;

export default TicketSummaryCard;
